import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilterList  
} from "@fortawesome/pro-regular-svg-icons";
import { useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";
import useExporter from "../../hooks/useExporter";
import PriceFormatter from "../../helpers/PriceFormater";
import { getDomains } from "../../api/Agreement";

const DomainsPage = () => {
  const { handleExport } = useExporter();
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [domains, setDomains] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => { },
  });

  useEffect(() => {
    handleGetDomainsByAgreement();
  }, []);

  const handleExportToExcel = () => {
    const domainsToExport =
      selectedDomains.length > 0
        ? filteredDomains.filter((domain) =>
          selectedDomains.includes(domain.nameSrsItemId)
        )
        : filteredDomains;

    if (domainsToExport.length === 0) {
      toast.error("No domains selected");
      return;
    }

    const domainsData = domainsToExport.map(
      ({
        nameSrsItemId,
        domainName,
        price,
        total,
        currency,
        renewalDate,
        autoRenew,
      }) => ({
        "Domain ID": nameSrsItemId,
        "Name": domainName,
        "Price": price,
        "Total ex. VAT": total,
        "Currency": currency,
        "Renewal date": renewalDate,
        "Auto-renewal": autoRenew,
      })
    );
    console.log("Exporting Domain data:", domainsData);

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Domain_${currentDate}`;

    handleExport({ Domain: domainsData }, filename);
  };

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = domains;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = domains.filter(
          (domain) =>
            domain.nameSrsItemId?.toLowerCase().includes(searchLower) ||
            domain.name?.toLowerCase().includes(searchLower) ||
            domain.price?.toLowerCase().includes(searchLower) ||
            domain.updatedBy?.toLowerCase().includes(searchLower) ||
            domain.createdBy?.toLowerCase().includes(searchLower)
        );
      }

      const sorted = [...filtered].sort((a, b) => {
        const aValue = a[sortColumn]?.toString().toLowerCase();
        const bValue = b[sortColumn]?.toString().toLowerCase();
        return sortOrder === "asc"
          ? aValue > bValue
            ? 1
            : -1
          : aValue < bValue
            ? 1
            : -1;
      });

      setFilteredDomains(sorted);
    };

    applyFilterAndSort();
  }, [search, domains, sortColumn, sortOrder]);

  const handleGetDomainsByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getDomains(
        agreementPublicId,
        accessToken
      );

      data.forEach(domain => {
        domain.friendlyPrice = PriceFormatter(domain.price);
        domain.autoRenew = domain.autoRenew
          ? "Yes"
          : "No";
      });
      setDomains(data);
      setFilteredDomains(data);
      setDomainsLoaded(true);
    } catch (error) {
      console.error(error);
      setDomainsLoaded(true);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Domains"} loading={!domainsLoaded} />
            </div>

            <div className={`${!domainsLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Domains</h2>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
               
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      {/* DOLD */}
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"name, domain"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredDomains}
                    selectedRows={selectedDomains}
                    setSelectedRows={setSelectedDomains}
                    noEdit={true}
                    disableLink={true}
                    noDelete={false}
                    columns={[
                      { key: "domainName", label: "Domain name" },
                      {
                        key: "price",
                        label: "Monthly price",
                      },
                      {
                        key: "total",
                        label: "Total ex. VAT",
                      },
                      {
                        key: "currency",
                        label: "Currency",
                      },
                      {
                        key: "renewalDate",
                        label: "Renewal date",
                      },
                      {
                        key: "autoRenew",
                        label: "Auto-renewal",
                      },
                    ]}
                    primaryKey="nameSrsItemId"
                    basePath={`/${agreementPublicId}/domains/edit/`}
                />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default DomainsPage;
